import type { InferResponseType } from 'hono'
import * as v from 'valibot'
import { client } from '@/shared/api/client'
import { readAvatarFailure, reportAvatarFailure } from '@/shared/lib/avatarDiagnostics'
import { assertValidVrmBlob } from '../lib/vrmBlobValidation'

export type HubAuthHeaders = () => Promise<Record<string, string>>
const hub = client.api.avatar.hub
export type HubModel = Extract<InferResponseType<(typeof hub.models)[':id']['$get']>, { id: string }>
export type HubStatus = Extract<InferResponseType<typeof hub.status.$get>, { connected: boolean }>
export type HubReturnResult = Extract<InferResponseType<typeof hub.auth.result.$get>, { purpose: string }>
export type HubCategory = 'own' | 'hearts' | 'staff_picks'
export type HubIssue = { code: 'HUB_AUTH_EXPIRED' | 'HUB_TERMS_CHANGED'; modelId: string; avatarVersion: string }
const errorSchema = v.object({ error: v.string(), code: v.string() })

/** Hub由来の失敗原因をHTTPステータスと分けて扱う。 */
export class HubApiError extends Error {
  constructor(
    public readonly code: string,
    public readonly status: number
  ) {
    super('VRoid Hubの操作に失敗しました。')
    this.name = 'HubApiError'
  }
}
const checkResponse = async (response: { ok: boolean; status: number; json: () => Promise<unknown> }) => {
  if (response.ok) return
  const parsed = v.safeParse(errorSchema, await response.json().catch(() => null))
  throw new HubApiError(parsed.success ? parsed.output.code : 'HUB_REQUEST_FAILED', response.status)
}
const options = async (auth: HubAuthHeaders, key?: string) => ({
  headers: { ...(await auth()), ...(key ? { 'Idempotency-Key': key } : {}) },
  init: { cache: 'no-store' as const },
})
export const getHubStatus = async (auth: HubAuthHeaders) => {
  const response = await hub.status.$get({}, await options(auth))
  await checkResponse(response)
  return response.json()
}
export const startHubAuth = async (auth: HubAuthHeaders, purpose: 'selection' | 'reauthentication') => {
  const response = await hub.auth.start.$post({ json: { purpose } }, await options(auth))
  await checkResponse(response)
  const result = await response.json()
  if (!('authorizationUrl' in result)) throw new HubApiError('HUB_REQUEST_FAILED', response.status)
  return result.authorizationUrl
}
export const getHubAuthResult = async (auth: HubAuthHeaders) => {
  const response = await hub.auth.result.$get({}, await options(auth))
  if (response.status === 204) return null
  await checkResponse(response)
  const result = await response.json()
  return 'purpose' in result ? result : null
}
export const getHubModels = async (auth: HubAuthHeaders, category: HubCategory, cursor?: string) => {
  const response = await hub.models.$get(
    { query: { kind: category, ...(cursor ? { cursor } : {}) } },
    await options(auth)
  )
  await checkResponse(response)
  const result = await response.json()
  if (!('models' in result)) throw new HubApiError('HUB_REQUEST_FAILED', response.status)
  return result
}
export const getHubModel = async (auth: HubAuthHeaders, id: string): Promise<HubModel> => {
  const response = await hub.models[':id'].$get({ param: { id } }, await options(auth))
  await checkResponse(response)
  const result = await response.json()
  if (!('id' in result)) throw new HubApiError('HUB_REQUEST_FAILED', response.status)
  return result
}
/** 一時配信先だけを使用し、Hubバイナリを永続ストレージへ書き込まない。 */
export const fetchHubBlob = async (downloadUrl: string): Promise<Blob> => {
  let blob: Blob
  try {
    const url = new URL(downloadUrl)
    if (url.protocol !== 'https:') throw new HubApiError('HUB_REQUEST_FAILED', 502)
    const response = await fetch(url.href, { credentials: 'omit', cache: 'no-store', referrerPolicy: 'no-referrer' })
    if (!response.ok)
      throw new HubApiError(response.status === 429 ? 'HUB_RATE_LIMITED' : 'HUB_REQUEST_FAILED', response.status)
    blob = await response.blob()
  } catch (error) {
    const { status, code } = readAvatarFailure(error)
    reportAvatarFailure('blob_download', status, code ?? 'NETWORK_ERROR')
    throw error
  }
  try {
    await assertValidVrmBlob(blob, 'VRoid Hub')
  } catch (error) {
    reportAvatarFailure('blob_validation', undefined, 'INVALID_VRM')
    throw error
  }
  return blob
}
export const getHubAvatarBlob = async (auth: HubAuthHeaders): Promise<Blob> => {
  let downloadUrl: string
  try {
    const response = await hub.download.$get({}, await options(auth))
    await checkResponse(response)
    const result = await response.json()
    if (!('downloadUrl' in result)) throw new HubApiError('HUB_REQUEST_FAILED', response.status)
    downloadUrl = result.downloadUrl
  } catch (error) {
    const { status, code } = readAvatarFailure(error)
    reportAvatarFailure('hub_download_url', status, code ?? 'NETWORK_ERROR')
    throw error
  }
  return fetchHubBlob(downloadUrl)
}
export const prepareHubAvatar = async (auth: HubAuthHeaders, model: HubModel, key: string) => {
  const response = await hub.prepare.$post(
    { json: { modelId: model.id, termsHash: model.termsHash, accepted: true } },
    await options(auth, key)
  )
  await checkResponse(response)
  const result = await response.json()
  if (!('ticket' in result)) throw new HubApiError('HUB_REQUEST_FAILED', response.status)
  return result
}
export const confirmHubAvatar = async (auth: HubAuthHeaders, ticket: string, key: string) => {
  const response = await hub.confirm.$put({ json: { ticket } }, await options(auth, key))
  await checkResponse(response)
  const result = await response.json()
  if (!('avatarVersion' in result)) throw new HubApiError('HUB_REQUEST_FAILED', response.status)
  return result
}
export const consentHubAvatar = async (auth: HubAuthHeaders, model: HubModel, avatarVersion: string, key: string) => {
  const response = await hub.consent.$put(
    { json: { modelId: model.id, avatarVersion, termsHash: model.termsHash, accepted: true } },
    await options(auth, key)
  )
  await checkResponse(response)
}
export const deleteHubSelection = async (auth: HubAuthHeaders, modelId: string, avatarVersion: string, key: string) => {
  const response = await hub.selection.$delete({ json: { modelId, avatarVersion } }, await options(auth, key))
  await checkResponse(response)
}
export const disconnectHub = async (auth: HubAuthHeaders, key: string) => {
  const response = await hub.connection.$delete({}, await options(auth, key))
  await checkResponse(response)
}
